import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useOnClickOutside } from "usehooks-ts";
import { useLazyQuery, gql } from "@apollo/client";

import Input from "components/Input";
import Switch from "components/Switch";
import { VEHICLE_NUMBER_TYPES } from "constants/signup";
import useDebounce from "hooks/useDebounce";
import { IOption } from "models/Form";
import { IPaymentProfile } from "models/HaulPay";
import { getUserByFmcsaAPI } from "services/api/user";
import { selectUserDetails } from "store/user/selector";
import PaymentProfile from "../PaymentProfile";
import styles from "./style.module.scss";
import { showErrorMessage } from "helpers/toastMessage";

type McDotSelectorProps = {
  register: any;
  errors: any;
  getValues: any;
  setPaymentProfile: (profile: Partial<IPaymentProfile>) => void;
  selectedCarrierType?: IOption;
  setSelectedCarrierType?: React.Dispatch<React.SetStateAction<IOption>>;
  isSearchCompanyName?: boolean;
  fmcsaEnabled?: boolean;
  useGroupIC?: boolean;
  updateFMCSAInfo?: (data: any) => void;
  name?: string;
};

export const SEARCH_PAYMENT_PROFILE = gql`
  query SearchPaymentProfile(
    $factoringId: String
    $status: String
    $carrierDba: String
    $first: Int
    $myCarriers: Boolean
    $mc: String
    $dot: String
  ) {
    searchPaymentProfile(
      factoringId: $factoringId
      status: $status
      carrierDba: $carrierDba
      first: $first
      myCarriers: $myCarriers
      mc: $mc
      dot: $dot
    ) {
      totalCount
      data {
        status
        companyName
        ownerEmail
        isConnected
        id
        companyMc
        companyDot
        factoringFeeSplitRate
        payoutDays
        ownerNameFirst
        ownerNameLast
        ownerPhoneNumber
        authority
        discountRate
        factoringCompany {
          id
          userId
          accountNumber
          method
          status
          name
          relationshipId
          noaAttachment {
            id
            filename
            category
          }
        }
        accounts {
          id
          method
          accountNumberRedacted
        }
        enablePayViaCheck
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const mapGraphQLToPaymentProfile = (gqlProfile: any): Partial<IPaymentProfile> => {
  return {
    id: gqlProfile.id,
    company_name: gqlProfile.companyName,
    company_mc: gqlProfile.companyMc,
    company_dot: gqlProfile.companyDot,
    owner_email: gqlProfile.ownerEmail,
    owner_name_first: gqlProfile.ownerNameFirst,
    owner_name_last: gqlProfile.ownerNameLast,
    status: gqlProfile.status.toLowerCase(),
    authority: gqlProfile.authority,
    factoring_company: gqlProfile.factoringCompany,
    is_connected: gqlProfile.isConnected,
    relationship_id: gqlProfile.factoringCompany?.relationshipId || null,
    accounts: gqlProfile.accounts,
    factoring_fee_split_rate: gqlProfile.factoringFeeSplitRate,
    payout_days: gqlProfile.payoutDays,
    enable_pay_via_check: gqlProfile.enablePayViaCheck,
    owner_phone_number_cell: gqlProfile.ownerPhoneNumber
    // discountRate: gqlProfile.discountRate
  };
};

export default function CarrierSelector({
  register,
  errors,
  getValues,
  setPaymentProfile,
  selectedCarrierType,
  setSelectedCarrierType,
  isSearchCompanyName = false,
  fmcsaEnabled = false,
  updateFMCSAInfo,
  useGroupIC = false,
  name = "identifyNumber"
}: Readonly<McDotSelectorProps>) {
  const userDetails = useSelector(selectUserDetails);
  const searchModalRef = useRef<HTMLDivElement>(null);
  const [isShowCarriersDropdown, setIsShowCarriersDropdown] = useState(false);
  const [carrierIDFilterString, setCarrierIDFilterString] = useState("");
  const debouncedIDFilterString = useDebounce(carrierIDFilterString, 500);
  const activeFetchId = useRef(0);
  const placeholder = selectedCarrierType?.value === "mc" ? "Enter MC#" : "Enter DOT#";

  const [fetchProfiles, { data, loading: isLoadingCarriers }] =
    useLazyQuery(SEARCH_PAYMENT_PROFILE);

  const getFMCSAInfo = async () => {
    if (updateFMCSAInfo) {
      try {
        const { data: fmcsaData } = await getUserByFmcsaAPI({
          [selectedCarrierType?.value ?? "mc"]: getValues("identifyNumber")
        });
        updateFMCSAInfo(fmcsaData);
      } catch (err) {
        updateFMCSAInfo(null);
      }
    }
  };

  const searchPaymentProfiles = async (value: string) => {
    let filter: any = { carrierDba: value };

    if (selectedCarrierType?.value === "mc") {
      filter = { mc: value };
    } else if (selectedCarrierType?.value === "dot") {
      filter = { dot: value };
    }

    try {
      const currentId = ++activeFetchId.current;

      await fetchProfiles({
        variables: {
          factoringId: userDetails?.factoring_id,
          status: "approved,needs_review,pending,declined",
          myCarriers: useGroupIC,
          first: 10,
          ...filter
        }
      });

      if (currentId !== activeFetchId.current) {
        return;
      }
    } catch (e) {
      showErrorMessage("Error Fetching Carrier.");
    }
  };

  const paymentProfileList: IPaymentProfile[] =
    data?.searchPaymentProfile?.data?.map(mapGraphQLToPaymentProfile) || [];

  useOnClickOutside([searchModalRef], () => {
    setIsShowCarriersDropdown(false);
    if (fmcsaEnabled) {
      getFMCSAInfo();
    }
  });

  useEffect(() => {
    if (debouncedIDFilterString !== "") {
      searchPaymentProfiles(debouncedIDFilterString);
    }
  }, [debouncedIDFilterString]);

  return (
    <div ref={searchModalRef} className={styles.container}>
      <div className="flex w-full desktop:relative">
        <Input
          autoComplete={"off"}
          name={name}
          register={register}
          error={errors[name]}
          isLoading={isLoadingCarriers}
          placeholder={!isSearchCompanyName ? placeholder : ""}
          onClick={() => setIsShowCarriersDropdown(true)}
          inputClass="!border-foreground !py-3.5"
          className="flex-1 company-btn"
          onChange={(e) => {
            const v = e.target.value;
            setCarrierIDFilterString(v);
            if (v.trim() === "") {
              setPaymentProfile({});
            }
          }}
        />

        {isShowCarriersDropdown ? (
          <div className={styles.dropdown}>
            {paymentProfileList.length
              ? paymentProfileList.map((paymentProfile) => (
                  <PaymentProfile
                    key={paymentProfile.id}
                    payment_profile={paymentProfile}
                    selectPaymentProfile={() => {
                      setPaymentProfile(paymentProfile);
                      setIsShowCarriersDropdown(false);
                    }}
                  />
                ))
              : null}
          </div>
        ) : null}
      </div>

      {setSelectedCarrierType && !isSearchCompanyName ? (
        <Switch data={VEHICLE_NUMBER_TYPES} setSelectedValue={setSelectedCarrierType} />
      ) : null}
    </div>
  );
}
